"use client";

import { Menu as MenuIcon } from "@mui/icons-material";
import { Box, Typography, Drawer, useMediaQuery, useTheme, IconButton } from "@mui/material";
import DividerFull from '@mui/material/Divider';
import Image from "next/image";
import { usePathname, useRouter } from 'next/navigation';
import { useTranslations } from "next-intl";
import * as React from "react";
import { useEffect, useRef, useState, useCallback } from "react";
import { Sidebar, Menu, MenuItem } from "react-pro-sidebar";
import { useSelector } from 'react-redux';
import { RootState } from '@/store/types';

// MUI

// Utils & Theme
import { colors } from '../../theme/colors';
import { isAIListingGeneratorEnabled, isSalesRankOnlyStore } from '../../utils/featureFlags';
import { openCrispChat } from '../CrispChat.jsx';
import styles from './Sidebar.module.css';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

// Types imported from @/store/types

// ============================================================================
// CONSTANTS
// ============================================================================

const SIDEBAR_WIDTH = {
  expanded: '260px',
  collapsed: '70px',
  mobile: 280
} as const;
const TRANSITION = {
  duration: '280ms',
  timing: 'cubic-bezier(0.22, 1, 0.36, 1)',
  // Premium ease-out
  labelDelay: '100ms'
} as const;

// Theme-derived colors (no more hardcoded values)
const COLORS = {
  text: {
    default: colors.text.tertiary,
    // #6B7280
    collapsed: colors.text.placeholder // #9CA3AF
  },
  background: {
    default: colors.background.paper // #FFFFFF
  },
  border: {
    default: colors.border.light // #E5E7EB
  },
  badge: {
    background: colors.cta.main,
    // #AC1218 (brand red)
    text: '#FFFFFF'
  },
  hover: {
    background: colors.primary.main,
    // #475569
    text: '#FFFFFF'
  }
} as const;

// ============================================================================
// COMPONENT
// ============================================================================

function SidebarComponent(): React.ReactElement {
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const sb = useTranslations('sidebar');
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const pathname = usePathname();
  const router = useRouter();
  const isDashboard = pathname?.endsWith('/dashboard');

  // ========================================================================
  // EVENT HANDLERS
  // ========================================================================

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        setCollapsed(true);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Listen for custom events to control sidebar (e.g. from Tour)
  useEffect(() => {
    const handleOpenSidebar = (): void => setCollapsed(false);
    const handleCloseSidebar = (): void => setCollapsed(true);
    window.addEventListener('descripio-sidebar-open', handleOpenSidebar);
    window.addEventListener('descripio-sidebar-close', handleCloseSidebar);
    return () => {
      window.removeEventListener('descripio-sidebar-open', handleOpenSidebar);
      window.removeEventListener('descripio-sidebar-close', handleCloseSidebar);
    };
  }, []);
  const handleMouseEnter = useCallback((): void => {
    setCollapsed(false);
  }, []);
  const handleMouseLeave = useCallback((): void => {
    setCollapsed(true);
  }, []);
  const handleMobileToggle = useCallback((): void => {
    setMobileOpen(prev => !prev);
  }, []);
  const handleMobileClose = useCallback((): void => {
    setMobileOpen(false);
  }, []);
  const handleShowTour = useCallback((e: React.MouseEvent): void => {
    e.preventDefault();
    if (isDashboard) {
      router.replace('/dashboard?replay=true');
    } else {
      router.push('/dashboard?replay=true');
    }
    if (isMobile) {
      handleMobileClose();
    }
  }, [isDashboard, isMobile, router, handleMobileClose]);
  const handleOpenChat = useCallback((e: React.MouseEvent): void => {
    e.preventDefault();
    openCrispChat();
    if (isMobile) {
      handleMobileClose();
    }
  }, [isMobile, handleMobileClose]);

  // ========================================================================
  // RENDER HELPERS
  // ========================================================================

  const getIcon = (name: string, isHovered: boolean): React.ReactElement => {
    const path = isHovered ? `/assets/svg/sidebaricon/${name}-white.svg` : `/assets/svg/sidebaricon/${name}.svg`;
    return <Image src={path} alt={`${name} icon`} width={20} height={20} data-sentry-element="Image" data-sentry-component="getIcon" data-sentry-source-file="Sidebar.tsx" />;
  };

  // ========================================================================
  // ANIMATED BADGE COMPONENT
  // ========================================================================

  const AnimatedBadge: React.FC<{
    text: string;
    visible: boolean;
  }> = ({
    text,
    visible
  }) => <Box className={styles.badge} sx={{
    backgroundColor: COLORS.badge.background,
    color: COLORS.badge.text,
    fontSize: '9px',
    fontWeight: 'bold',
    padding: '2px 6px',
    borderRadius: '8px',
    textTransform: 'uppercase',
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    alignSelf: 'flex-start',
    // Motion Trinity: Physically-modelled spring animation
    opacity: visible ? 1 : 0,
    transform: visible ? 'scale(1) translateY(0)' : 'scale(0.8) translateY(-4px)',
    transition: `
                    opacity 200ms ${TRANSITION.timing} 150ms,
                    transform 250ms cubic-bezier(0.34, 1.56, 0.64, 1) 150ms
                `,
    pointerEvents: visible ? 'auto' : 'none'
  }} data-sentry-element="Box" data-sentry-component="AnimatedBadge" data-sentry-source-file="Sidebar.tsx">
            {text}
        </Box>;

  // ========================================================================
  // ANIMATED LABEL COMPONENT
  // ========================================================================

  const AnimatedLabel: React.FC<{
    children: React.ReactNode;
    visible: boolean;
    extraPadding?: boolean;
  }> = ({
    children,
    visible,
    extraPadding = false
  }) => <Box sx={{
    textOverflow: 'unset',
    fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
    paddingLeft: extraPadding && !visible ? '25px' : '0px',
    // Motion Trinity: Synchronized text fade
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateX(0)' : 'translateX(-8px)',
    transition: `
                    opacity 180ms ease-out ${visible ? '100ms' : '0ms'},
                    transform 200ms ${TRANSITION.timing} ${visible ? '80ms' : '0ms'}
                `
  }} data-sentry-element="Box" data-sentry-component="AnimatedLabel" data-sentry-source-file="Sidebar.tsx">
            {children}
        </Box>;

  // ========================================================================
  // MENU CONTENT RENDERER
  // ========================================================================

  const renderMenuContent = (isMobileDrawer: boolean = false): React.ReactElement => {
    const showLabels = !collapsed || isMobileDrawer;
    const textColor = collapsed && !isMobileDrawer ? COLORS.text.collapsed : COLORS.text.default;
    return <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 'auto',
      overflowY: isMobileDrawer ? 'auto' : 'visible'
    }} data-sentry-element="Box" data-sentry-component="renderMenuContent" data-sentry-source-file="Sidebar.tsx">
                {/* Menu Title - Logo only in mobile drawer (desktop has header logo) */}
                {isMobileDrawer && <Menu>
                        <Box sx={{
          textAlign: 'center',
          display: 'flex',
          justifyContent: 'center',
          color: COLORS.text.default,
          paddingTop: '5px',
          fontWeight: 'bold'
        }}>
                            <Image src="/assets/svg/login/genslogo.webp" width={48} height={48} alt="Descripio Logo" style={{
            objectFit: "contain"
          }} priority />
                        </Box>
                    </Menu>}

                <Box sx={{
        flex: '1 1 auto'
      }} data-sentry-element="Box" data-sentry-source-file="Sidebar.tsx">
                    {/* Main Navigation Menu */}
                    <Menu style={{
          marginTop: "5px"
        }} onMouseEnter={!isMobileDrawer ? handleMouseEnter : undefined} onMouseLeave={!isMobileDrawer ? handleMouseLeave : undefined} data-sentry-element="Menu" data-sentry-source-file="Sidebar.tsx">
                        {/* 1. Dashboard */}
                        <MenuItem href="/dashboard" style={{
            marginTop: "0px",
            textDecoration: "none",
            color: textColor
          }} icon={getIcon("home", hoveredItem === "/dashboard")} onMouseEnter={() => {
            setHoveredItem("/dashboard");
            if (!isMobileDrawer) handleMouseEnter();
          }} onMouseLeave={() => {
            setHoveredItem(null);
            if (!isMobileDrawer) handleMouseLeave();
          }} onClick={isMobileDrawer ? handleMobileClose : undefined} data-sentry-element="MenuItem" data-sentry-source-file="Sidebar.tsx">
                            <AnimatedLabel visible={showLabels} data-sentry-element="AnimatedLabel" data-sentry-source-file="Sidebar.tsx">
                                {sb("Dashboard")}
                            </AnimatedLabel>
                        </MenuItem>

                        {/* 2. AI Image Generator */}
                        <MenuItem href="/image-generator" style={{
            marginTop: "0px",
            textDecoration: "none",
            color: textColor
          }} icon={getIcon("image", hoveredItem === "/image-generator")} onMouseEnter={() => {
            setHoveredItem("/image-generator");
            if (!isMobileDrawer) handleMouseEnter();
          }} onMouseLeave={() => {
            setHoveredItem(null);
            if (!isMobileDrawer) handleMouseLeave();
          }} onClick={isMobileDrawer ? handleMobileClose : undefined} data-sentry-element="MenuItem" data-sentry-source-file="Sidebar.tsx">
                            <AnimatedLabel visible={showLabels} data-sentry-element="AnimatedLabel" data-sentry-source-file="Sidebar.tsx">
                                {sb("AIImageGenerator")}
                            </AnimatedLabel>
                        </MenuItem>

                        {/* 3. AI Listing Generator */}
                        {isAIListingGeneratorEnabled(authUser?.store?.id ?? '') && <MenuItem href="/ai-listing-generator" style={{
            marginTop: "0px",
            textDecoration: "none",
            color: textColor
          }} icon={getIcon("layer", hoveredItem === "/ai-listing-generator")} onMouseEnter={() => {
            setHoveredItem("/ai-listing-generator");
            if (!isMobileDrawer) handleMouseEnter();
          }} onMouseLeave={() => {
            setHoveredItem(null);
            if (!isMobileDrawer) handleMouseLeave();
          }} onClick={isMobileDrawer ? handleMobileClose : undefined}>
                                <AnimatedLabel visible={showLabels}>
                                    {sb("AIListingGenerator")}
                                </AnimatedLabel>
                            </MenuItem>}

                        {/* 4. Listing Optimization */}
                        {!isSalesRankOnlyStore(authUser?.store?.id ?? '') && <MenuItem href="/product-listing" style={{
            marginTop: "0px",
            textDecoration: "none",
            color: textColor
          }} icon={getIcon("package", hoveredItem === "/product-listing")} onMouseEnter={() => {
            setHoveredItem("/product-listing");
            if (!isMobileDrawer) handleMouseEnter();
          }} onMouseLeave={() => {
            setHoveredItem(null);
            if (!isMobileDrawer) handleMouseLeave();
          }} onClick={isMobileDrawer ? handleMobileClose : undefined}>
                                <AnimatedLabel visible={showLabels}>
                                    {sb("menuHeading")}
                                </AnimatedLabel>
                            </MenuItem>}

                        {/* 5. Upload Status */}
                        {!isSalesRankOnlyStore(authUser?.store?.id ?? '') && !!authUser?.store?.id && authUser?.store?.skip_now !== true && authUser?.store?.recently_using_market_place !== null && <MenuItem href="/feed-status" style={{
            marginTop: "0px",
            textDecoration: "none",
            color: textColor
          }} icon={getIcon("Asin", hoveredItem === "/feed-status")} onMouseEnter={() => {
            setHoveredItem("/feed-status");
            if (!isMobileDrawer) handleMouseEnter();
          }} onMouseLeave={() => {
            setHoveredItem(null);
            if (!isMobileDrawer) handleMouseLeave();
          }} onClick={isMobileDrawer ? handleMobileClose : undefined}>
                                <AnimatedLabel visible={showLabels}>
                                    {sb("FeedStatus")}
                                </AnimatedLabel>
                            </MenuItem>}

                        {/* 6. Review Analyzer (formerly URL Analyzer) */}
                        {!isSalesRankOnlyStore(authUser?.store?.id ?? '') && <MenuItem href="/url-listing-analyzer" style={{
            marginTop: "0px",
            textDecoration: "none",
            color: textColor
          }} icon={getIcon("star", hoveredItem === "/url-listing-analyzer")} onMouseEnter={() => {
            setHoveredItem("/url-listing-analyzer");
            if (!isMobileDrawer) handleMouseEnter();
          }} onMouseLeave={() => {
            setHoveredItem(null);
            if (!isMobileDrawer) handleMouseLeave();
          }} onClick={isMobileDrawer ? handleMobileClose : undefined}>
                                <AnimatedLabel visible={showLabels}>
                                    {sb("ReviewAnalyzer")}
                                </AnimatedLabel>
                            </MenuItem>}

                        {/* 7. Listing Monitoring */}
                        {!isSalesRankOnlyStore(authUser?.store?.id ?? '') && <MenuItem href="/listing-monitoring" style={{
            marginTop: "0px",
            textDecoration: "none",
            color: textColor
          }} icon={getIcon("Content", hoveredItem === "/listing-monitoring")} onMouseEnter={() => {
            setHoveredItem("/listing-monitoring");
            if (!isMobileDrawer) handleMouseEnter();
          }} onMouseLeave={() => {
            setHoveredItem(null);
            if (!isMobileDrawer) handleMouseLeave();
          }} onClick={isMobileDrawer ? handleMobileClose : undefined}>
                                <AnimatedLabel visible={showLabels}>
                                    {sb("menuItem4")}
                                </AnimatedLabel>
                            </MenuItem>}

                        {/* 8. Sales Rank Monitoring */}
                        <MenuItem href="/sales-rank-monitoring" style={{
            marginTop: "0px",
            textDecoration: "none",
            color: textColor
          }} icon={getIcon("flag", hoveredItem === "/sales-rank-monitoring")} onMouseEnter={() => {
            setHoveredItem("/sales-rank-monitoring");
            if (!isMobileDrawer) handleMouseEnter();
          }} onMouseLeave={() => {
            setHoveredItem(null);
            if (!isMobileDrawer) handleMouseLeave();
          }} onClick={isMobileDrawer ? handleMobileClose : undefined} data-sentry-element="MenuItem" data-sentry-source-file="Sidebar.tsx">
                            <AnimatedLabel visible={showLabels} data-sentry-element="AnimatedLabel" data-sentry-source-file="Sidebar.tsx">
                                {sb("SalesRankMonitoring")}
                            </AnimatedLabel>
                        </MenuItem>

                    </Menu>
                </Box>

                {/* Bottom section - settings and logout */}
                <Box sx={{
        flex: '0 0 auto',
        borderTop: isMobileDrawer ? `1px solid ${COLORS.border.default}` : 'none',
        pt: isMobileDrawer ? 1 : 0,
        pb: 2,
        position: isMobileDrawer ? 'sticky' : 'static',
        bottom: 0,
        backgroundColor: COLORS.background.default
      }} data-sentry-element="Box" data-sentry-source-file="Sidebar.tsx">
                    <Menu data-sentry-element="Menu" data-sentry-source-file="Sidebar.tsx">
                        {/* ASIN Management */}
                        {!isSalesRankOnlyStore(authUser?.store?.id ?? '') && <MenuItem href="/on-boarding" style={{
            textDecoration: "none",
            color: textColor
          }} icon={getIcon("database", hoveredItem === "/on-boarding")} onMouseEnter={() => {
            setHoveredItem("/on-boarding");
            if (!isMobileDrawer) handleMouseEnter();
          }} onMouseLeave={() => {
            setHoveredItem(null);
            if (!isMobileDrawer) handleMouseLeave();
          }} onClick={isMobileDrawer ? handleMobileClose : undefined}>
                                <AnimatedLabel visible={showLabels} extraPadding>
                                    {sb("menuItem3")}
                                </AnimatedLabel>
                            </MenuItem>}

                        {/* Review API */}
                        <MenuItem href="/developer/api-keys" style={{
            textDecoration: "none",
            color: textColor
          }} icon={getIcon("key", hoveredItem === "/developer/api-keys")} onMouseEnter={() => {
            setHoveredItem("/developer/api-keys");
            if (!isMobileDrawer) handleMouseEnter();
          }} onMouseLeave={() => {
            setHoveredItem(null);
            if (!isMobileDrawer) handleMouseLeave();
          }} onClick={isMobileDrawer ? handleMobileClose : undefined} data-sentry-element="MenuItem" data-sentry-source-file="Sidebar.tsx">
                            <AnimatedLabel visible={showLabels} extraPadding data-sentry-element="AnimatedLabel" data-sentry-source-file="Sidebar.tsx">
                                {sb("ReviewApi")}
                            </AnimatedLabel>
                        </MenuItem>

                        {/* Settings */}
                        <MenuItem href="/settings" style={{
            textDecoration: "none",
            color: textColor
          }} icon={getIcon("settings", hoveredItem === "/settings")} onMouseEnter={() => {
            setHoveredItem("/settings");
            if (!isMobileDrawer) handleMouseEnter();
          }} onMouseLeave={() => {
            setHoveredItem(null);
            if (!isMobileDrawer) handleMouseLeave();
          }} onClick={isMobileDrawer ? handleMobileClose : undefined} data-sentry-element="MenuItem" data-sentry-source-file="Sidebar.tsx">
                            <AnimatedLabel visible={showLabels} extraPadding data-sentry-element="AnimatedLabel" data-sentry-source-file="Sidebar.tsx">
                                {sb("Settings")}
                            </AnimatedLabel>
                        </MenuItem>

                        {/* Help & Support — opens email on desktop, chat on mobile */}
                        <MenuItem href={isMobileDrawer ? undefined : `mailto:e-commerce@raab.de?subject=${sb("SupportRequest")}`} onClick={isMobileDrawer ? handleOpenChat : undefined} style={{
            textDecoration: "none",
            color: textColor,
            cursor: "pointer"
          }} icon={getIcon("help-circle", hoveredItem === "help-support")} onMouseEnter={() => {
            setHoveredItem("help-support");
            if (!isMobileDrawer) handleMouseEnter();
          }} onMouseLeave={() => {
            setHoveredItem(null);
            if (!isMobileDrawer) handleMouseLeave();
          }} data-sentry-element="MenuItem" data-sentry-source-file="Sidebar.tsx">
                            <AnimatedLabel visible={showLabels} extraPadding data-sentry-element="AnimatedLabel" data-sentry-source-file="Sidebar.tsx">
                                {sb("HelpSupport")}
                            </AnimatedLabel>
                        </MenuItem>

                        <DividerFull sx={{
            maxWidth: collapsed && !isMobileDrawer ? "60px" : "100%",
            padding: "5px 0",
            borderBottomWidth: "2px",
            transition: `max-width ${TRANSITION.duration} ${TRANSITION.timing}`
          }} data-sentry-element="DividerFull" data-sentry-source-file="Sidebar.tsx" />

                        {/* Logout */}
                        <MenuItem href="/" style={{
            textDecoration: "none",
            color: textColor
          }} icon={getIcon("log-out", hoveredItem === "/")} onMouseEnter={() => {
            setHoveredItem("/");
            if (!isMobileDrawer) handleMouseEnter();
          }} onMouseLeave={() => {
            setHoveredItem(null);
            if (!isMobileDrawer) handleMouseLeave();
          }} onClick={isMobileDrawer ? handleMobileClose : undefined} data-sentry-element="MenuItem" data-sentry-source-file="Sidebar.tsx">
                            <AnimatedLabel visible={showLabels} extraPadding data-sentry-element="AnimatedLabel" data-sentry-source-file="Sidebar.tsx">
                                {sb("Logout")}
                            </AnimatedLabel>
                        </MenuItem>
                    </Menu>
                </Box>
            </Box>;
  };

  // ========================================================================
  // MAIN RENDER
  // ========================================================================

  return <>
            {/* Desktop Sidebar - Hidden on mobile */}
            {!isMobile && <Box id="Sidebar" ref={sidebarRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className={`${styles.sidebarContainer} ${collapsed ? styles.collapsed : styles.expanded}`} sx={{
      top: "72px",
      height: "calc(100vh - 72px)",
      display: "flex",
      position: "fixed",
      zIndex: 1150,
      borderRight: collapsed ? "1px solid white" : `1px solid ${COLORS.border.default}`,
      backgroundColor: COLORS.background.default,
      // Motion Trinity: Fluid transitions
      transition: `
                            border-color ${TRANSITION.duration} ${TRANSITION.timing},
                            box-shadow ${TRANSITION.duration} ${TRANSITION.timing}
                        `,
      boxShadow: collapsed ? 'none' : colors.shadows.small
    }}>
                    <Sidebar collapsed={collapsed} width={SIDEBAR_WIDTH.expanded} collapsedWidth={SIDEBAR_WIDTH.collapsed} className={styles.sidebar} style={{
        height: "100%",
        zIndex: 3
      }} transitionDuration={280}>
                        {renderMenuContent(false)}
                    </Sidebar>
                </Box>}

            {/* Mobile Hamburger Button - Only visible when drawer is closed */}
            {isMobile && !mobileOpen && <IconButton onClick={handleMobileToggle} sx={{
      position: 'fixed',
      top: 16,
      left: 16,
      zIndex: 1300,
      backgroundColor: COLORS.background.default,
      boxShadow: colors.shadows.medium,
      // Motion Trinity: Tactile feedback
      transition: 'transform 150ms cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 150ms ease-out',
      '&:hover': {
        backgroundColor: colors.background.hover,
        transform: 'scale(1.05)'
      },
      '&:active': {
        transform: 'scale(0.95)'
      }
    }}>
                    <MenuIcon />
                </IconButton>}

            {/* Mobile Drawer */}
            {isMobile && <Drawer variant="temporary" anchor="left" open={mobileOpen} onClose={handleMobileClose} ModalProps={{
      keepMounted: true
    }} sx={{
      display: {
        xs: 'block',
        md: 'none'
      },
      '& .MuiDrawer-paper': {
        boxSizing: 'border-box',
        width: SIDEBAR_WIDTH.mobile,
        backgroundColor: COLORS.background.default,
        borderRight: `1px solid ${COLORS.border.default}`,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto'
      }
    }}>
                    <Box sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        pt: 2,
        flex: 1,
        overflowY: 'auto'
      }} role="presentation">
                        <Sidebar collapsed={false} style={{
          height: 'auto',
          border: 'none',
          backgroundColor: 'transparent'
        }}>
                            {renderMenuContent(true)}
                        </Sidebar>
                    </Box>
                </Drawer>}
        </>;
}
export default SidebarComponent;